/**
 * PendingRedemptionBanner Component
 *
 * Displays a warning banner when the user has been granted access to tenants
 * that require B2B invitation redemption (consent). Shows for federated
 * organization users who need to complete the consent flow before accessing
 * managed tenants.
 *
 * The backend automatically verifies redemption status when fetching the
 * tenant list, so the tenant will appear automatically after the user
 * completes consent and refreshes.
 */

import { makeStyles, shorthands, tokens, Button, Link } from '@fluentui/react-components';
import { Warning16Filled, Open16Regular } from '@fluentui/react-icons';
import type { PendingTenant } from '../../types/tenant';

export interface PendingRedemptionBannerProps {
  tenants: PendingTenant[];
}

const useStyles = makeStyles({
  banner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...shorthands.padding('12px', '16px'),
    backgroundColor: tokens.colorStatusWarningBackground1,
    ...shorthands.borderBottom('1px', 'solid', tokens.colorStatusWarningBorder1),
    ...shorthands.gap('12px'),
  },
  content: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('8px'),
    flexGrow: 1,
  },
  icon: {
    color: tokens.colorStatusWarningForeground1,
    flexShrink: 0,
  },
  text: {
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    color: tokens.colorNeutralForeground1,
  },
  tenantName: {
    fontWeight: tokens.fontWeightSemibold,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('8px'),
    flexShrink: 0,
  },
});

/**
 * Opens the redemption URL in a new window.
 */
function openRedemptionUrl(tenant: PendingTenant) {
  if (tenant.invite_redeem_url) {
    window.open(tenant.invite_redeem_url, '_blank', 'noopener,noreferrer');
  }
}

export function PendingRedemptionBanner({ tenants }: PendingRedemptionBannerProps) {
  const styles = useStyles();

  if (tenants.length === 0) {
    return null;
  }

  // Handle single tenant case
  if (tenants.length === 1) {
    const tenant = tenants[0];
    return (
      <div className={styles.banner} role="alert" data-testid="pending-redemption-banner">
        <div className={styles.content}>
          <Warning16Filled className={styles.icon} />
          <span className={styles.text}>
            You have been granted access to{' '}
            <span className={styles.tenantName}>{tenant.tenant_name}</span>.{' '}
            {tenant.invite_redeem_url ? (
              <>
                Please{' '}
                <Link inline onClick={() => openRedemptionUrl(tenant)}>
                  complete Microsoft consent
                </Link>{' '}
                to activate access.
              </>
            ) : (
              'Consent is required to activate access.'
            )}
          </span>
        </div>
        {tenant.invite_redeem_url && (
          <div className={styles.actions}>
            <Button
              appearance="primary"
              size="small"
              icon={<Open16Regular />}
              onClick={() => openRedemptionUrl(tenant)}
            >
              Complete Setup
            </Button>
          </div>
        )}
      </div>
    );
  }

  // Handle multiple tenants case
  const firstTenant = tenants[0];
  return (
    <div className={styles.banner} role="alert" data-testid="pending-redemption-banner">
      <div className={styles.content}>
        <Warning16Filled className={styles.icon} />
        <span className={styles.text}>
          You have been granted access to {tenants.length} tenants that require Microsoft consent.
          Complete the consent process for each tenant to activate access.
        </span>
      </div>
      {firstTenant?.invite_redeem_url && (
        <div className={styles.actions}>
          <Button
            appearance="primary"
            size="small"
            icon={<Open16Regular />}
            onClick={() => openRedemptionUrl(firstTenant)}
          >
            Complete Setup ({tenants.length})
          </Button>
        </div>
      )}
    </div>
  );
}
